import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";
import {
  Users,
  Workflow,
  LineChart,
  Code,
  Sparkles,
  Zap,
  Moon,
  Sun,
} from "lucide-react";
import { useSession } from "@/hooks/use-session";
import { CalendlyEmbed } from "./CalendlyEmbed";
import { Hero } from "./Hero";
import { ParadigmManifestoSection } from "./ParadigmManifestoSection";
import { useState, useEffect } from "react";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";
import { updateThemeMode } from "@/lib/theme-utils";
import { SiteNavbar } from "./SiteNavbar";
import { SiteLink } from "./SiteLink";

export function LandingPage() {
  const { login } = useSession();
  const [showCalendly, setShowCalendly] = useState(false);
  const themeMode = usePreferencesStore((state) => state.themeMode);
  const setThemeMode = usePreferencesStore((state) => state.setThemeMode);

  // Scroll to top on mount (for client-side navigation)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const toggleTheme = () => {
    const newMode = themeMode === "dark" ? "light" : "dark";
    setThemeMode(newMode);
    updateThemeMode(newMode);
  };

  // Custom right content for homepage
  const homepageRightContent = (
    <div className="flex items-center gap-3">
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
      >
        {themeMode === "dark" ? (
          <Moon className="h-5 w-5" />
        ) : (
          <Sun className="h-5 w-5" />
        )}
      </Button>
      <Button
        variant="ghost"
        onClick={() => login()}
        className="text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-black dark:hover:text-white"
      >
        Log in
      </Button>
      <Button
        asChild
        size="sm"
        className="text-sm font-medium bg-black hover:bg-black/90 dark:bg-white dark:hover:bg-white/90 dark:text-black text-white"
      >
        <SiteLink href="/docs">Build a flow</SiteLink>
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen w-full bg-white dark:bg-black scroll-smooth">
      {/* Top Navigation */}
      <SiteNavbar variant="homepage" rightContent={homepageRightContent} />

      {/* Hero Section */}
      <Hero />

      {/* Problem/Solution Section - HIDDEN FOR STEALTH MODE */}
      {false && (
        <section className="px-6 py-16 md:py-24 bg-white dark:bg-black">
          <div className="mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-black dark:text-white">
                AI App Development Shouldn't Be Code-Only
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {[
                {
                  icon: Code,
                  title: "Engineers working in silos",
                  description:
                    "Development happens in isolation without input from product and design",
                },
                {
                  icon: Sparkles,
                  title: "PMs lack visibility",
                  description:
                    "Product managers can't see or influence app capabilities and experiences",
                },
                {
                  icon: Workflow,
                  title: "Designers can't iterate",
                  description:
                    "Design teams have no way to prototype and refine AI interactions",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                >
                  <Card className="h-full border-gray-200 dark:border-gray-800 hover:border-[#6633FF]/30 transition-colors bg-white dark:bg-black">
                    <CardContent className="p-6">
                      <div className="mb-4 inline-flex p-3 rounded-lg bg-gray-100 dark:bg-gray-900">
                        <item.icon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2 text-black dark:text-white">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <p className="text-xl md:text-2xl font-medium">
                <span className="text-gray-600 dark:text-gray-400">
                  Taias brings your whole team into the{" "}
                </span>
                <span className="bg-gradient-to-r from-[#00CCFF] to-[#00CC66] bg-clip-text text-transparent font-semibold">
                  AI app building process
                </span>
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* Features Grid - HIDDEN FOR STEALTH MODE */}
      {false && (
        <section className="px-6 py-16 md:py-24 bg-gray-50 dark:bg-gray-950">
          <div className="mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-black dark:text-white">
                Built for the Whole Team
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Product-first tools that empower everyone to contribute
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Users,
                  title: "Collaborative Building",
                  description:
                    "Craft AI app experiences together—no code barriers between team members",
                  gradientFrom: "#FF6A00",
                  gradientTo: "#FF0099",
                },
                {
                  icon: Zap,
                  title: "Product-First Tools",
                  description:
                    "Design user flows, test interactions, and validate experiences before shipping",
                  gradientFrom: "#FF0099",
                  gradientTo: "#6633FF",
                },
                {
                  icon: LineChart,
                  title: "Team Alignment",
                  description:
                    "Keep engineers, PMs, and designers in sync throughout the development cycle",
                  gradientFrom: "#00CCFF",
                  gradientTo: "#00CC66",
                },
              ].map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <Card className="h-full border-gray-200 dark:border-gray-800 hover:border-[#6633FF]/30 transition-all hover:shadow-lg group cursor-default bg-white dark:bg-black">
                    <CardContent className="p-8">
                      <div
                        className="mb-6 inline-flex p-[2px] rounded-xl"
                        style={{
                          background: `linear-gradient(to bottom right, ${feature.gradientFrom}, ${feature.gradientTo})`,
                        }}
                      >
                        <div className="p-4 rounded-[10px] bg-white dark:bg-black">
                          <feature.icon className="w-8 h-8 text-black dark:text-white" />
                        </div>
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-black dark:text-white">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Platform Support */}
      <section className="border-t border-zinc-100 dark:border-zinc-800 bg-white dark:bg-black">
        <div className="mx-auto flex max-w-3xl items-center justify-center px-6 py-8 sm:py-9 md:py-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex flex-wrap justify-center items-center gap-8"
          >
            <img
              src="/claude_logo.svg"
              alt="Claude"
              className="h-8 opacity-40 dark:opacity-60 dark:invert grayscale hover:grayscale-0 hover:opacity-100 dark:hover:opacity-100 transition-all"
            />
            <img
              src="/openai_logo.svg"
              alt="OpenAI"
              className="h-8 opacity-40 dark:opacity-60 dark:invert grayscale hover:grayscale-0 hover:opacity-100 dark:hover:opacity-100 transition-all"
            />
            <img
              src="/mcp.svg"
              alt="MCP"
              className="h-8 opacity-40 dark:opacity-60 dark:invert grayscale hover:grayscale-0 hover:opacity-100 dark:hover:opacity-100 transition-all"
            />
          </motion.div>
        </div>
      </section>

      {/* Paradigm Manifesto Section */}
      <ParadigmManifestoSection />

      {/* Final CTA - HIDDEN FOR STEALTH MODE */}
      {false && (
        <section className="px-6 py-16 md:py-24 bg-white dark:bg-black">
          <div className="mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-black dark:text-white">
                Ready to Transform Your AI App Development?
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-10 max-w-2xl mx-auto">
                Join teams building the next generation of AI applications
              </p>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  size="lg"
                  onClick={() => setShowCalendly(true)}
                  className="px-10 py-7 text-xl font-semibold relative overflow-hidden group bg-black hover:bg-black/90 dark:bg-white dark:hover:bg-white/90 dark:text-black shadow-lg hover:shadow-xl transition-shadow"
                >
                  <span className="relative z-10">Book a Demo</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#FF6A00] via-[#FF0099] via-[#6633FF] to-[#00CCFF] opacity-0 group-hover:opacity-20 transition-opacity" />
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Footer - HIDDEN FOR STEALTH MODE */}
      {false && (
        <footer className="px-6 py-12 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-black">
          <div className="mx-auto max-w-6xl text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              © 2025 Taias. All rights reserved.
            </p>
          </div>
        </footer>
      )}

      {/* Calendly Modal */}
      <CalendlyEmbed
        open={showCalendly}
        onClose={() => setShowCalendly(false)}
      />
    </div>
  );
}
